import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { thinkspacesService } from '@/services/collections/thinkspaces';
import { useAuth } from '@/contexts/AuthContext';
import type {
  ThinkSpace,
  CanvasElement,
  PostItNote,
  TextElement,
  LineElement,
  BoxElement,
  EllipseElement,
  DiamondElement,
  ConnectorElement,
  ImageElement,
  CreatePostItInput,
  CreateTextInput,
  CreateLineInput,
  CreateBoxInput,
  CreateEllipseInput,
  CreateDiamondInput,
  CreateConnectorInput,
  CreateImageInput,
  InviteUserInput,
  SharePermission,
} from '@/types/thinkspace';
import { Timestamp } from 'firebase/firestore';
import { calculateSmartAngularRoute } from '@/utils/connectorUtils';

export const useThinkSpace = (id: string) => {
  const { user } = useAuth();
  const [thinkSpace, setThinkSpace] = useState<ThinkSpace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pendingUpdates = useRef<CanvasElement[] | null>(null);
  const localChangedIds = useRef<Set<string>>(new Set()); // Track elements with local changes
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);

  // Merge remote changes with local pending changes
  const mergeElements = useCallback((
    remoteElements: CanvasElement[],
    localElements: CanvasElement[] | null
  ): CanvasElement[] => {
    if (!localElements || localChangedIds.current.size === 0) {
      // No local changes pending, use remote as-is
      return remoteElements;
    }

    // Check for locally deleted elements
    const locallyDeletedIds = new Set<string>();
    for (const changeId of localChangedIds.current) {
      if (changeId.startsWith('deleted:')) {
        locallyDeletedIds.add(changeId.replace('deleted:', ''));
      }
    }

    // Create a map of remote elements by id
    const remoteMap = new Map(remoteElements.map(el => [el.id, el]));
    const mergedMap = new Map<string, CanvasElement>();

    // First, add all remote elements (except locally deleted ones)
    for (const el of remoteElements) {
      if (!locallyDeletedIds.has(el.id)) {
        mergedMap.set(el.id, el);
      }
    }

    // Then, for elements with local changes, compare timestamps and keep newer
    for (const localEl of localElements) {
      if (localChangedIds.current.has(localEl.id)) {
        const remoteEl = remoteMap.get(localEl.id);
        if (!remoteEl) {
          // Element was deleted remotely - check if our change is meaningful
          // If we have a local change, keep it (will be synced on next save)
          mergedMap.set(localEl.id, localEl);
        } else {
          // Both exist - compare updatedAt timestamps
          const localTime = localEl.updatedAt?.toMillis?.() || 0;
          const remoteTime = remoteEl.updatedAt?.toMillis?.() || 0;
          if (localTime >= remoteTime) {
            // Local is newer or same, keep local
            mergedMap.set(localEl.id, localEl);
          }
          // Otherwise remote is newer, already in mergedMap
        }
      }
    }

    // Handle elements that exist locally but not remotely (newly added locally)
    for (const localEl of localElements) {
      if (!remoteMap.has(localEl.id) && localChangedIds.current.has(localEl.id)) {
        mergedMap.set(localEl.id, localEl);
      }
    }

    return Array.from(mergedMap.values());
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    const unsubscribe = thinkspacesService.subscribe(
      id,
      (space) => {
        if (space) {
          setThinkSpace((current) => {
            // Merge remote changes with local pending changes
            const mergedElements = mergeElements(
              space.elements,
              current?.elements || null
            );
            return { ...space, elements: mergedElements };
          });
        } else {
          setThinkSpace(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [id, mergeElements]);

  // Check access permissions
  const access = thinkSpace && user
    ? thinkspacesService.checkAccess(thinkSpace, user.uid, user.email || '')
    : { hasAccess: false, canEdit: false, isOwner: false };

  // Flush pending updates on unmount
  useEffect(() => {
    return () => {
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
      // Fire and forget - we can't await in cleanup
      const pending = pendingUpdates.current;
      if (pending && id) {
        thinkspacesService.updateElements(id, pending).catch(console.error);
      }
    };
  }, [id]);

  // Debounced save to Firestore
  const saveElements = useCallback(
    async (elements: CanvasElement[], changedElementId?: string) => {
      if (!id || !access.canEdit) {
        return;
      }

      // Track which element was changed locally
      if (changedElementId) {
        localChangedIds.current.add(changedElementId);
      }

      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }

      pendingUpdates.current = elements;

      updateTimeout.current = setTimeout(async () => {
        try {
          const pending = pendingUpdates.current;
          if (pending) {
            await thinkspacesService.updateElements(id, pending);
            pendingUpdates.current = null;
            // Clear local change tracking after successful save
            localChangedIds.current.clear();
          }
        } catch (err) {
          console.error('Failed to save elements:', err);
        }
      }, 300);
    },
    [id, access.canEdit]
  );

  // Get next z-index
  const getNextZIndex = useCallback(() => {
    if (!thinkSpace?.elements.length) return 1;
    return Math.max(...thinkSpace.elements.map((e) => e.zIndex)) + 1;
  }, [thinkSpace?.elements]);

  // Add post-it note
  const addPostIt = useCallback(
    (input: CreatePostItInput) => {
      if (!thinkSpace || !user || !access.canEdit) {
        return;
      }

      const postIt: PostItNote = {
        id: crypto.randomUUID(),
        type: 'postit',
        title: input.title,
        content: input.content,
        color: input.color,
        position: input.position || { x: 100, y: 100 },
        size: { width: 200, height: 200 },
        zIndex: getNextZIndex(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, postIt];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, postIt.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add text element
  const addText = useCallback(
    (input: CreateTextInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const textEl: TextElement = {
        id: crypto.randomUUID(),
        type: 'text',
        content: input.content,
        fontSize: input.fontSize || 16,
        fontFamily: input.fontFamily || 'Space Grotesk',
        color: input.color || '#151414',
        bold: false,
        italic: false,
        position: input.position || { x: 100, y: 100 },
        zIndex: getNextZIndex() - 1000, // Default behind post-its
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, textEl];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, textEl.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add line element
  const addLine = useCallback(
    (input: CreateLineInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const line: LineElement = {
        id: crypto.randomUUID(),
        type: 'line',
        position: input.startPosition,
        endPosition: input.endPosition,
        strokeColor: input.strokeColor || '#151414',
        strokeWidth: input.strokeWidth || 2,
        zIndex: getNextZIndex() - 1000,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, line];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, line.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add box element
  const addBox = useCallback(
    (input: CreateBoxInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const box: BoxElement = {
        id: crypto.randomUUID(),
        type: 'box',
        position: input.position,
        size: input.size,
        fillColor: input.fillColor ?? null,
        strokeColor: input.strokeColor || '#151414',
        strokeWidth: input.strokeWidth || 2,
        borderRadius: input.borderRadius || 0,
        zIndex: getNextZIndex() - 1000,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, box];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, box.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add ellipse element
  const addEllipse = useCallback(
    (input: CreateEllipseInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const ellipse: EllipseElement = {
        id: crypto.randomUUID(),
        type: 'ellipse',
        position: input.position,
        size: input.size,
        fillColor: input.fillColor ?? null,
        strokeColor: input.strokeColor || '#151414',
        strokeWidth: input.strokeWidth || 2,
        zIndex: getNextZIndex() - 1000,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, ellipse];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, ellipse.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add diamond element
  const addDiamond = useCallback(
    (input: CreateDiamondInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const diamond: DiamondElement = {
        id: crypto.randomUUID(),
        type: 'diamond',
        position: input.position,
        size: input.size,
        fillColor: input.fillColor ?? null,
        strokeColor: input.strokeColor || '#151414',
        strokeWidth: input.strokeWidth || 2,
        zIndex: getNextZIndex() - 1000,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, diamond];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, diamond.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add connector element
  const addConnector = useCallback(
    (input: CreateConnectorInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      // Calculate routing points based on start/end positions
      const routingPoints = calculateSmartAngularRoute(
        input.startPosition,
        input.endPosition
      );

      const connector: ConnectorElement = {
        id: crypto.randomUUID(),
        type: 'connector',
        position: input.startPosition, // Position is the start point
        startConnection: input.startConnection ?? null,
        endConnection: input.endConnection ?? null,
        startPosition: input.startPosition,
        endPosition: input.endPosition,
        strokeColor: input.strokeColor || '#151414',
        strokeWidth: input.strokeWidth || 2,
        routingPoints,
        zIndex: getNextZIndex() - 2000, // Connectors below shapes
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, connector];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, connector.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Add image element
  const addImage = useCallback(
    (input: CreateImageInput) => {
      if (!thinkSpace || !user || !access.canEdit) return;

      const image: ImageElement = {
        id: crypto.randomUUID(),
        type: 'image',
        imageUrl: input.imageUrl,
        imagePath: input.imagePath,
        position: input.position || { x: 100, y: 100 },
        size: input.size,
        opacity: input.opacity ?? 1,
        borderRadius: input.borderRadius ?? 0,
        zIndex: getNextZIndex(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid,
      };

      const newElements = [...thinkSpace.elements, image];
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, image.id);
    },
    [thinkSpace, user, access.canEdit, getNextZIndex, saveElements]
  );

  // Update element
  const updateElement = useCallback(
    (elementId: string, updates: Partial<CanvasElement>) => {
      if (!thinkSpace || !access.canEdit) return;

      const newElements = thinkSpace.elements.map((el) =>
        el.id === elementId
          ? ({ ...el, ...updates, updatedAt: Timestamp.now() } as CanvasElement)
          : el
      );
      setThinkSpace({ ...thinkSpace, elements: newElements });
      saveElements(newElements, elementId);
    },
    [thinkSpace, access.canEdit, saveElements]
  );

  // Delete element
  const deleteElement = useCallback(
    (elementId: string) => {
      if (!thinkSpace || !access.canEdit) return;

      const newElements = thinkSpace.elements.filter((el) => el.id !== elementId);
      setThinkSpace({ ...thinkSpace, elements: newElements });
      // Track deletion by adding to localChangedIds (we'll use a special marker)
      localChangedIds.current.add(`deleted:${elementId}`);
      saveElements(newElements);
    },
    [thinkSpace, access.canEdit, saveElements]
  );

  // Z-index operations
  const bringToFront = useCallback(
    (elementId: string) => {
      if (!thinkSpace || !access.canEdit) return;

      const maxZ = Math.max(...thinkSpace.elements.map((e) => e.zIndex));
      updateElement(elementId, { zIndex: maxZ + 1 });
    },
    [thinkSpace, access.canEdit, updateElement]
  );

  const sendToBack = useCallback(
    (elementId: string) => {
      if (!thinkSpace || !access.canEdit) return;

      const minZ = Math.min(...thinkSpace.elements.map((e) => e.zIndex));
      updateElement(elementId, { zIndex: minZ - 1 });
    },
    [thinkSpace, access.canEdit, updateElement]
  );

  const bringForward = useCallback(
    (elementId: string) => {
      if (!thinkSpace || !access.canEdit) return;

      const element = thinkSpace.elements.find((e) => e.id === elementId);
      if (!element) return;

      const higherElements = thinkSpace.elements
        .filter((e) => e.zIndex > element.zIndex)
        .sort((a, b) => a.zIndex - b.zIndex);

      if (higherElements.length > 0) {
        const nextElement = higherElements[0];
        updateElement(elementId, { zIndex: nextElement.zIndex + 1 });
      }
    },
    [thinkSpace, access.canEdit, updateElement]
  );

  const sendBackward = useCallback(
    (elementId: string) => {
      if (!thinkSpace || !access.canEdit) return;

      const element = thinkSpace.elements.find((e) => e.id === elementId);
      if (!element) return;

      const lowerElements = thinkSpace.elements
        .filter((e) => e.zIndex < element.zIndex)
        .sort((a, b) => b.zIndex - a.zIndex);

      if (lowerElements.length > 0) {
        const prevElement = lowerElements[0];
        updateElement(elementId, { zIndex: prevElement.zIndex - 1 });
      }
    },
    [thinkSpace, access.canEdit, updateElement]
  );

  return {
    thinkSpace,
    loading,
    error,
    access,
    addPostIt,
    addText,
    addLine,
    addBox,
    addEllipse,
    addDiamond,
    addConnector,
    addImage,
    updateElement,
    deleteElement,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
  };
};

// Sharing mutations
export const useInviteUser = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ thinkSpaceId, input }: { thinkSpaceId: string; input: InviteUserInput }) =>
      thinkspacesService.inviteUser(thinkSpaceId, input, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};

export const useUpdatePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      thinkSpaceId,
      userEmail,
      permission,
    }: {
      thinkSpaceId: string;
      userEmail: string;
      permission: SharePermission;
    }) => thinkspacesService.updatePermission(thinkSpaceId, userEmail, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};

export const useRemoveSharedUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ thinkSpaceId, userEmail }: { thinkSpaceId: string; userEmail: string }) =>
      thinkspacesService.removeSharedUser(thinkSpaceId, userEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};
